// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import BEST_GENDER_AGNOSTIC_PIN_IMAGES from 'app/common/react/components/growth/activation/NuxConst/bestGenderAgnosticPinImages';
import type { NuxStep } from 'app/common/redux/actions/nuxProgress';
import type { State as ReduxState } from 'app/common/redux/types';
import UnauthUsage from 'app/common/lib/UnauthUsage';
import { BACKGROUND_IMAGE_INDICES_FOR_NUX_STEP } from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Image, Mask, Masonry } from 'gestalt';
import { getGridItemGutter, MIN_COLUMNS } from 'app/common/lib/react/gridUtils';
import { SingleTreatmentExp, MultiTreatmentExp } from 'app/common/lib/experiment';

type ImageDimensionsInfo = {|
  height: number,
  url: string,
  width: number,
|};

type ReduxStateProps = {|
  nuxSelectedInterestImages?: ?Array<ImageDimensionsInfo>,
  nuxStep?: ?NuxStep,
|};

type ReduxDispatchProps = {|
  // $FlowFixMe: Please add "import type { Dispatch } from 'redux';"
  dispatch: Dispatch<any>,
|};

type HOCProps = {|
  requestContext: RequestContextType,
|};

type OwnProps = {|
  disableFlexibleBGPins: boolean,
  isInNux?: boolean,
  pinImages?: Array<ImageDimensionsInfo>,
|};

type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
  ...ReduxDispatchProps,
  ...HOCProps,
|};

type State = {|
  loading: boolean,
  lpPinImages: Array<ImageDimensionsInfo>,
  shouldLoadProgressively: boolean,
|};

class BackgroundPinImages extends PureComponent<Props, State> {
  state = {
    loading: true,
    lpPinImages: [],
    shouldLoadProgressively: false,
  };

  // Used for setting number of Pin images, based on indices defined in the const, to show based on NUX step
  progressiveBackgroundImageIndices: Array<number> = BACKGROUND_IMAGE_INDICES_FOR_NUX_STEP.welcome;
  // Used for setting topic images as background images, gets set when user is on topic picker and selected interest(s)
  selectedTopicImages: Array<ImageDimensionsInfo> = [];

  componentDidMount() {
    const { isInNux, pinImages = [], requestContext } = this.props;

    const unauthRelatedPinImages = this.getUnauthPinImages() || [];
    const unauthLastVisitedPinImage = unauthRelatedPinImages[0];

    const unauthVisitedPinImages = this.getUnauthPinImages(true) || [];

    let addRelatedPinImages = false;
    let addVisitedPinImages = false;

    if (unauthVisitedPinImages.length > 1) {
      if (isInNux) {
        const moreRelevantBackgroundPinsExp = new SingleTreatmentExp(
          requestContext,
          'web_nux_more_relevant_bg_pins',
        );

        if (moreRelevantBackgroundPinsExp.enabled()) {
          addVisitedPinImages = true;
        }
      } else {
        const homeLoginPageRelevantBackgroundPinsExp = new MultiTreatmentExp(
          requestContext,
          'web_nux_home_login_page_relevant_bg_pins',
        );

        // This current iteration only triggers users with 2 visited pins
        // If enabled_related_pins performs better, run a follow-up to trigger users with 1 visited pin
        if (homeLoginPageRelevantBackgroundPinsExp.check('enabled_related_pins')) {
          addRelatedPinImages = true;
        }

        if (homeLoginPageRelevantBackgroundPinsExp.check('enabled_visited_pins')) {
          addVisitedPinImages = true;
        }
      }
    }

    let lpPinImages = [...pinImages];

    if (addRelatedPinImages) {
      lpPinImages = [...unauthRelatedPinImages, ...lpPinImages];
    }

    if (addVisitedPinImages) {
      lpPinImages = [...unauthVisitedPinImages, ...lpPinImages];
    }

    const isPinFromPreviousSession =
      (unauthVisitedPinImages[0] || {}).url !== (unauthLastVisitedPinImage || {}).url;
    if (isInNux && isPinFromPreviousSession) {
      lpPinImages = [unauthLastVisitedPinImage, ...lpPinImages];
    }

    const progressiveNuxBackgroundPinsExp = new SingleTreatmentExp(
      requestContext,
      'web_nux_progressive_bg_pins',
    );
    const inProgressiveNuxBackgroundPinsExp = isInNux && progressiveNuxBackgroundPinsExp.enabled();

    const newState = {
      loading: false,
      lpPinImages,
      shouldLoadProgressively: inProgressiveNuxBackgroundPinsExp,
    };

    // If props `pinImages` didn't supply enough images, add static gender agnostic pin images
    if (newState.lpPinImages.length < 15) {
      newState.lpPinImages = newState.lpPinImages.concat(BEST_GENDER_AGNOSTIC_PIN_IMAGES);
    }

    setTimeout(() => this.setState(newState));
  }

  UNSAFE_componentWillUpdate(nextProps) {
    const { isInNux, nuxSelectedInterestImages, nuxStep, requestContext } = nextProps;
    const { lpPinImages, shouldLoadProgressively } = this.state;

    // Update image indices if background images should load progressively
    if (nuxStep && shouldLoadProgressively) {
      this.progressiveBackgroundImageIndices = BACKGROUND_IMAGE_INDICES_FOR_NUX_STEP[nuxStep];
    }

    const topicFlipExp = new MultiTreatmentExp(requestContext, 'web_nux_picker_flip');
    const showSelectedTopicBackground = !topicFlipExp.checkWithNoActivation('enabled_flip_no_bg');

    // If user is on topic picker step in NUX, update selected topics' images
    if (isInNux && nuxStep === 'topic' && showSelectedTopicBackground) {
      // Default selected topic images to a copy of the current state of background pins. We need to reset the list
      // everytime nuxSelectedInterestImages is updated since we revert background images back to the original list
      // when user unselects topics
      this.selectedTopicImages = Array.from(lpPinImages);
      if (nuxSelectedInterestImages) {
        for (
          let i = 0;
          i < nuxSelectedInterestImages.length && i < this.selectedTopicImages.length;
          i += 1
        ) {
          this.selectedTopicImages[i] = nuxSelectedInterestImages[i];
        }
      }
    }
  }

  shouldShowBackgroundWithSelectedTopicImages = (): boolean =>
    // Only show selected topic images background if we're in topic picker or loader step. selectedTopicImages
    // is only populated if we are in the topic picker bg pins experiment
    !!this.selectedTopicImages &&
    this.selectedTopicImages.length > 0 &&
    !!this.props.isInNux &&
    ['topic', 'loader'].includes(this.props.nuxStep);

  getUnauthPinImages = (sessionOnly: boolean = false): Array<ImageDimensionsInfo> => {
    const pins = sessionOnly ? UnauthUsage.getUnauthSessionsPins() : UnauthUsage.getUnauthPins();
    // Filter out Pin objects without valid image field, and return list of Pin images
    return pins.filter((pin) => pin.images && pin.images['236x']).map((pin) => pin.images['236x']);
  };

  renderPinImage = (obj: *) => {
    const { data: image } = obj;
    const { height, url, width } = image;

    return (
      <Mask shape="rounded" wash>
        <Image alt="pin-image" src={url} naturalWidth={width} naturalHeight={height} />
      </Mask>
    );
  };

  renderPinImageProgressively = (obj: *) => {
    const { data: image, itemIdx } = obj;
    const { height, url, width } = image;
    const isShownImage = this.progressiveBackgroundImageIndices.indexOf(itemIdx) >= 0;
    const backgroundPinImage = (
      <Mask shape="rounded" wash>
        <Image alt="pin-image" src={url} naturalWidth={width} naturalHeight={height}>
          {!isShownImage && (
            <Box
              dangerouslySetInlineStyle={{
                __style: {
                  backgroundColor: 'rgba(2, 4, 4, 0.6)',
                  paddingBottom: `${(height / width) * 100}%`,
                },
              }}
            />
          )}
        </Image>
      </Mask>
    );

    return backgroundPinImage;
  };

  render() {
    const { disableFlexibleBGPins } = this.props;
    const { loading, lpPinImages, shouldLoadProgressively } = this.state;

    return (
      !loading &&
      lpPinImages && (
        <Experiment name="web_homefeed_nux">
          {({ anyEnabled: inHomefeedExp }) => (
            <Box
              bottom
              left
              maxHeight={2500}
              overflow="hidden"
              paddingY={6}
              paddingX={5}
              position={inHomefeedExp ? 'relative' : 'absolute'}
              right
              top
              dangerouslySetInlineStyle={{
                __style: {
                  pointerEvents: 'none',
                },
              }}
            >
              <Masonry
                flexible={!disableFlexibleBGPins}
                comp={
                  shouldLoadProgressively ? this.renderPinImageProgressively : this.renderPinImage
                }
                gutterWidth={getGridItemGutter(true)}
                items={
                  this.shouldShowBackgroundWithSelectedTopicImages()
                    ? this.selectedTopicImages
                    : lpPinImages
                }
                minCols={MIN_COLUMNS}
              />
            </Box>
          )}
        </Experiment>
      )
    );
  }
}

const mapStateToProps = (state: ReduxState): ReduxStateProps => ({
  nuxSelectedInterestImages:
    state.ui.nuxSelectedInterestImages && state.ui.nuxSelectedInterestImages.images,
  nuxStep: state.ui.nuxProgress && state.ui.nuxProgress.step,
});

export default compose(
  connect<_, OwnProps, ReduxStateProps, ReduxDispatchProps, _, _>(
    mapStateToProps,
    null,
  ),
  withRequestContext,
)(BackgroundPinImages);
